import type { DispenseView, DispensedView, MedicineView, PrescriptionView } from './read';

/**
 * Where a prescription stands at a pharmacy.
 * waiting: nothing recorded yet.
 * partly: some medicines handed over, or some not dispensed here.
 * dispensed: every medicine handed over.
 * not_dispensed: the pharmacy recorded that it dispensed none of them.
 */
export type DispensingStatus = 'waiting' | 'partly' | 'dispensed' | 'not_dispensed';

/** One prescribed medicine and what happened to it. */
export interface MedicineProgress {
  medicine: MedicineView;
  /** Each time it was handed over, oldest first. */
  handedOver: { date: string; item: DispensedView }[];
  /** True when it was last recorded as not dispensed and was never handed over. */
  declined: boolean;
}

/** True when every item of the dispensing record answers a medicine of this prescription. */
export function dispenseMatchesPrescription(dispense: DispenseView, prescription: PrescriptionView): boolean {
  const requests = new Set(prescription.medicines.map((m) => m.requestId));
  return dispense.items.length > 0 && dispense.items.every((item) => (item.requestId ? requests.has(item.requestId) : item.prescriptionRef === prescription.id));
}

/** What the pharmacy's dispensing records add up to for one prescription, oldest record first. */
export function dispensingProgress(prescription: PrescriptionView, dispenses: readonly DispenseView[]): { status: DispensingStatus; medicines: MedicineProgress[] } {
  const ordered = [...dispenses].sort((a, b) => a.date.localeCompare(b.date));
  const medicines = prescription.medicines.map((medicine): MedicineProgress => {
    const handedOver: MedicineProgress['handedOver'] = [];
    let declined = false;
    for (const dispense of ordered) {
      for (const item of dispense.items) {
        if (item.requestId !== medicine.requestId) continue;
        if (item.status === 'completed') handedOver.push({ date: dispense.date, item });
        else declined = true;
      }
    }
    return { medicine, handedOver, declined: declined && handedOver.length === 0 };
  });
  const done = medicines.filter((m) => m.handedOver.length > 0).length;
  const status: DispensingStatus =
    ordered.length === 0
      ? 'waiting'
      : done === medicines.length
        ? 'dispensed'
        : done === 0 && medicines.every((m) => m.declined)
          ? 'not_dispensed'
          : 'partly';
  return { status, medicines };
}
